import { useState, useEffect, useCallback } from 'react';
import { UserRole } from "@/types/roles";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
  registered_at: string;
  meta_connected: boolean;
  role: UserRole | "pending";
  is_approved: boolean;
  created_by?: string;
}

export const useCustomers = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      console.log("Loading customer data from v_users view...");
      
      // Use the v_users view to get all user data
      const { data: usersData, error: usersError } = await supabase
        .from('v_users')
        .select('*');
        
      if (usersError) {
        console.error("Error fetching users:", usersError);
        throw usersError;
      }

      console.log("v_users data:", usersData);
      
      if (!usersData || usersData.length === 0) {
        console.log("No user data found");
        setCustomers([]);
        setLoading(false);
        return;
      }
      
      console.log(`Found ${usersData.length} users`);
      
      // Transform the user data into our Customer interface
      const customersData: Customer[] = usersData.map(user => {
        // Use effective_role which prioritizes user_roles table over profile role
        const effectiveRole = user.effective_role || 'user';
        
        console.log(`User ${user.email}: profile_role=${user.profile_role}, effective_role=${user.effective_role}`);
        
        return {
          id: user.id,
          name: user.name || user.email?.split('@')[0] || 'Unknown',
          email: user.email || '',
          phone: user.phone || null,
          status: user.is_approved ? 'active' : 'inactive',
          registered_at: user.registered_at,
          meta_connected: user.meta_connected || false,
          role: effectiveRole as UserRole | "pending",
          is_approved: user.is_approved || false,
          created_by: user.created_by || undefined
        };
      });
      
      console.log(`Transformed ${customersData.length} customers:`, customersData);
      setCustomers(customersData);
    } catch (error) {
      console.error("Error loading customers:", error);
      toast.error("Fehler beim Laden der Benutzerdaten");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    
    // Set up a subscription for real-time updates
    const usersChannel = supabase
      .channel('v_users-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'profiles' 
      }, () => {
        console.log("Profiles table changed, reloading customers...");
        loadCustomers();
      })
      .subscribe();
      
    // Also listen for changes in user_roles
    const rolesChannel = supabase
      .channel('user_roles-changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'user_roles' 
      }, () => {
        console.log("User roles table changed, reloading customers...");
        loadCustomers();
      })
      .subscribe();
      
    // Listen for auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      console.log("Auth state changed, reloading customers...");
      loadCustomers();
    });
    
    return () => {
      supabase.removeChannel(usersChannel);
      supabase.removeChannel(rolesChannel);
      authListener?.subscription.unsubscribe();
    };
  }, [loadCustomers]);
  
  const updateUserRole = async (userId: string, role: UserRole): Promise<boolean> => {
    try {
      console.log(`Updating user ${userId} role to ${role}`);
      
      // Update user_roles table - this is the primary source of truth
      const { error: userRoleError } = await supabase
        .from('user_roles')
        .upsert({ 
          user_id: userId, 
          role,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
        
      if (userRoleError) {
        console.error("Error updating user_roles:", userRoleError);
        throw userRoleError;
      }
      
      // Also update the profile role field for consistency
      const { error: profileUpdateError } = await supabase
        .from('profiles')
        .update({ 
          role,
          updated_at: new Date().toISOString() 
        })
        .eq('id', userId);
        
      if (profileUpdateError) {
        console.error("Error updating profile role:", profileUpdateError);
        // Continue even if this fails - the user_roles is the primary source of truth
      }
      
      console.log(`Successfully updated user ${userId} role to ${role}`);
      toast.success(`Benutzerrolle erfolgreich auf ${role} aktualisiert`);
      
      // Reload customers to reflect changes
      await loadCustomers();
      
      return true;
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Fehler beim Aktualisieren der Benutzerrolle");
      return false;
    }
  };
  
  const approveUser = async (userId: string): Promise<boolean> => {
    try {
      console.log(`Approving user ${userId}`);
      
      // Update the user's is_approved status in the profiles table
      const { error } = await supabase
        .from('profiles')
        .update({ 
          is_approved: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);
        
      if (error) {
        console.error("Error approving user in profiles:", error);
        throw error;
      }
      
      // If user was in 'pending' state, upgrade them to regular 'user'
      const customer = customers.find(c => c.id === userId);
      if (customer && customer.role === 'pending') {
        await updateUserRole(userId, 'user');
      }
      
      console.log(`Successfully approved user ${userId}`);
      toast.success("Benutzer erfolgreich freigeschaltet");
      
      // Reload customers to reflect changes
      await loadCustomers();
      
      return true;
    } catch (error) {
      console.error("Error approving user:", error);
      toast.error("Fehler beim Freischalten des Benutzers");
      return false;
    }
  };

  const deleteUser = async (userId: string): Promise<boolean> => {
    try {
      console.log(`=== STARTING USER DELETION PROCESS FOR: ${userId} ===`);
      
      // Get user info for logging
      const customer = customers.find(c => c.id === userId);
      console.log(`Deleting user: ${customer?.email} (${customer?.name})`);
      
      // Step 1: Delete from user_roles table
      console.log("Step 1: Deleting from user_roles...");
      const { error: rolesError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId);
        
      if (rolesError) {
        console.error("Error deleting user roles:", rolesError);
        // Continue even if this fails
      } else {
        console.log("✓ Successfully deleted from user_roles");
      }
      
      // Step 2: Delete from meta_connections if exists
      console.log("Step 2: Deleting from meta_connections...");
      const { error: metaError } = await supabase
        .from('meta_connections')
        .delete()
        .eq('user_id', userId);
        
      if (metaError) {
        console.error("Error deleting meta connections:", metaError);
        // Continue even if this fails
      } else {
        console.log("✓ Successfully deleted from meta_connections");
      }
      
      // Step 3: Delete from profiles table
      console.log("Step 3: Deleting from profiles...");
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', userId);
        
      if (profileError) {
        console.error("Error deleting user profile:", profileError);
        throw profileError;
      } else {
        console.log("✓ Successfully deleted from profiles");
      }
      
      // Step 4: Delete from Supabase Auth (this is critical!)
      console.log("Step 4: Deleting from Supabase Auth...");
      
      // Use the admin API to delete the user from auth.users
      const { error: authError } = await supabase.auth.admin.deleteUser(userId);
      
      if (authError) {
        console.error("CRITICAL: Error deleting auth user:", authError);
        toast.error(`Fehler beim Löschen des Auth-Benutzers: ${authError.message}`);
        // Don't return false here - the profile is already deleted
      } else {
        console.log("✓ Successfully deleted from auth.users");
      }
      
      console.log(`=== USER DELETION COMPLETED FOR: ${userId} ===`);
      toast.success(`Benutzer ${customer?.email} erfolgreich gelöscht`);
      
      // Reload customers to reflect changes
      await loadCustomers();
      
      return true;
    } catch (error) {
      console.error("=== USER DELETION FAILED ===", error);
      toast.error(`Fehler beim Löschen des Benutzers: ${error.message}`);
      return false;
    }
  };
  
  const filteredCustomers = customers.filter(customer => {
    if (!searchQuery) return true;
    
    return (
      customer.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (customer.phone && customer.phone.includes(searchQuery))
    );
  });
  
  const getFilteredCustomers = (filter: 'all' | 'active' | 'inactive' | 'admins' | 'pending') => {
    switch (filter) {
      case 'active':
        return filteredCustomers.filter(c => c.status === 'active');
      case 'inactive':
        return filteredCustomers.filter(c => c.status !== 'active');
      case 'admins':
        return filteredCustomers.filter(c => c.role === 'admin');
      case 'pending':
        return filteredCustomers.filter(c => c.role === 'pending');
      case 'all':
      default:
        return filteredCustomers;
    }
  };

  return {
    customers: filteredCustomers,
    loading,
    searchQuery,
    setSearchQuery,
    loadCustomers,
    updateUserRole,
    approveUser,
    deleteUser,
    getFilteredCustomers
  };
};
